import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import QRTicketDialog from "@/components/QRTicketDialog";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, CalendarDays, MapPin, RefreshCcw, Ticket } from "lucide-react";
import { toast } from "sonner";

const STATUS_LABEL = {
  confirmed: "CONFIRMED",
  scanned: "SCANNED",
  cancelled: "CANCELLED",
  refunded: "REFUNDED",
  partially_refunded: "PARTIAL REFUND",
};

export default function MyBookings() {
  const { user, loading } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [active, setActive] = useState(null);
  const [refunding, setRefunding] = useState(null); // booking pending confirm
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  const bookingGroups = useMemo(() => groupBookingsByEvent(bookings), [bookings]);

  const goBack = () => {
    if (window.history.length > 1) navigate(-1);
    else navigate("/");
  };

  const fetchBookings = () => api.get("/bookings").then((r) => setBookings(r.data || []));

  useEffect(() => {
    if (!user) return;
    fetchBookings();
  }, [user]);

  if (loading) return <div className="min-h-screen bg-background"><Navbar /></div>;
  if (!user) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="mx-auto max-w-md p-10 text-center">
          <Button variant="ghost" onClick={goBack} className="mb-6 rounded-full">
            <ArrowLeft className="mr-2 h-4 w-4" /> Back
          </Button>
          <h2 className="font-display text-2xl font-bold">Sign in to view your bookings</h2>
          <Button onClick={() => navigate("/login")} className="mt-4 rounded-full">Sign in</Button>
        </div>
      </div>
    );
  }

  const onRefund = async () => {
    if (!refunding) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/bookings/${refunding.booking_id}/refund`, {});
      toast.success(`£${refunding.total.toFixed(2)} refunded`);
      setRefunding(null);
      setActive(null);
      fetchBookings();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Refund failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="mx-auto max-w-5xl px-5 md:px-8 py-10">
        <Button variant="ghost" onClick={goBack} className="mb-4 -ml-3 rounded-full" data-testid="bookings-back-btn">
          <ArrowLeft className="mr-2 h-4 w-4" /> Back
        </Button>
        <h1 className="font-display text-3xl md:text-4xl font-black flex items-center gap-3"><Ticket className="h-7 w-7 text-primary" /> My Bookings</h1>
        <p className="text-muted-foreground mt-1 text-sm">All your tickets in one place — show the QR at the door.</p>

        {bookings.length === 0 ? (
          <div className="mt-10 p-10 rounded-2xl border border-dashed border-border text-center">
            <p className="text-muted-foreground">You haven't booked anything yet. Discover something tonight →</p>
            <Button onClick={() => navigate("/")} className="mt-4 rounded-full">Browse events</Button>
          </div>
        ) : (
          <div className="mt-8 space-y-10" data-testid="booking-event-groups">
            {bookingGroups.map((group) => (
              <section key={group.key} data-testid={`booking-event-${group.key}`}>
                <div className="mb-4 flex items-start gap-4 border-b border-border pb-4">
                  {group.poster && <img src={group.poster} alt="" loading="lazy" decoding="async" className="h-20 w-16 rounded-lg object-cover" />}
                  <div className="min-w-0 flex-1">
                    <p className="text-[10px] font-black uppercase tracking-[0.25em] text-primary">{group.type}</p>
                    <h2 className="mt-1 font-display text-xl font-black md:text-2xl">{group.title}</h2>
                    <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                      {(group.venue || group.city) && <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> {[group.venue, group.city].filter(Boolean).join(", ")}</span>}
                      {group.date && <span className="flex items-center gap-1"><CalendarDays className="h-3.5 w-3.5" /> {group.date}{group.time ? ` · ${group.time}` : ""}</span>}
                      <span>{group.bookings.length} booking{group.bookings.length === 1 ? "" : "s"}</span>
                    </div>
                  </div>
                </div>
                <div className="grid gap-4 sm:grid-cols-2">
                  {group.bookings.map((b) => (
                    <div key={b.booking_id} data-testid={`booking-card-${b.booking_id}`} className="ticket-stub overflow-hidden rounded-2xl border border-border bg-card transition hover:-translate-y-0.5">
                      <button onClick={() => setActive(b)} className="w-full p-4 text-left">
                        <p className="text-[10px] font-black uppercase tracking-[0.2em] text-primary">Ticket</p>
                        <p className="mt-1 font-display font-extrabold">{bookingTicketSummary(b)}</p>
                        <p className="mt-2 font-mono text-[10px] text-muted-foreground">{b.booking_id}</p>
                        <p className="mt-2 text-xs font-bold"><span className={bookingStatusClass(b.status)}>{STATUS_LABEL[b.status] || b.status?.toUpperCase()}</span></p>
                      </button>
                      <div className="flex items-center justify-between border-t border-border px-4 py-2">
                        <span className="text-xs font-semibold">£{Number(b.total || 0).toFixed(2)}</span>
                        {b.status === "confirmed" && <Button size="sm" variant="ghost" data-testid={`refund-btn-${b.booking_id}`} onClick={() => setRefunding(b)} className="h-8 rounded-full text-xs"><RefreshCcw className="mr-1 h-3.5 w-3.5" /> Cancel & refund</Button>}
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            ))}
          </div>
        )}
      </main>
      <QRTicketDialog open={!!active} onOpenChange={(o) => !o && setActive(null)} booking={active} />
      <AlertDialog open={!!refunding} onOpenChange={(o) => !o && setRefunding(null)}>
        <AlertDialogContent data-testid="refund-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel & refund?</AlertDialogTitle>
            <AlertDialogDescription>
              We'll refund <span className="font-bold">£{(refunding?.total || 0).toFixed(2)}</span> to your original payment method
              for <span className="font-bold">{refunding?.item_title}</span>. This can take 3–5 business days to appear.
              The QR ticket will be invalidated immediately.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy}>Keep booking</AlertDialogCancel>
            <AlertDialogAction onClick={onRefund} disabled={busy} data-testid="refund-confirm-btn">
              {busy ? "Processing…" : "Yes, refund"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function bookingStatusClass(status) {
  if (status === "scanned") return "text-destructive";
  if (status === "cancelled" || status === "refunded") return "text-muted-foreground";
  if (status === "partially_refunded") return "text-orange-500";
  return "text-primary";
}

function groupBookingsByEvent(bookings) {
  const groups = new Map();
  bookings.forEach((booking) => {
    const title = booking.item_title || booking.content_title || "Booked event";
    const key = booking.item_id || booking.event_id || `${booking.item_type || "event"}:${title}:${booking.date || ""}`;
    if (!groups.has(key)) {
      groups.set(key, {
        key,
        title,
        type: booking.item_type || booking.content_type || "event",
        poster: booking.item_poster || booking.poster_url || "",
        venue: booking.venue || booking.venue_name || "",
        city: booking.city || "",
        date: booking.date || "",
        time: booking.time || "",
        bookings: [],
      });
    }
    groups.get(key).bookings.push(booking);
  });
  return [...groups.values()];
}

function bookingTicketSummary(booking) {
  const items = Array.isArray(booking.ticket_items) ? booking.ticket_items : [];
  if (!items.length) return booking.seats?.length ? `Seats ${booking.seats.join(", ")}` : `${booking.category || "General"} x ${booking.quantity || 1}`;
  return items.map((item) => `${item.category} x ${item.quantity} · ${ticketDateLabel(item, booking.date)}`).join(" / ");
}

function ticketDateLabel(item, fallbackDate) {
  const dates = Array.isArray(item?.valid_dates) ? item.valid_dates.filter(Boolean) : [];
  if (item?.scope === "multi_day" || item?.scan_policy === "once_per_day") {
    return dates.length > 1
      ? `${formatShortDate(dates[0])} to ${formatShortDate(dates[dates.length - 1])}`
      : formatShortDate(dates[0] || fallbackDate);
  }
  return formatShortDate(dates[0] || fallbackDate);
}

function formatShortDate(value) {
  if (!value) return "Event day";
  const date = new Date(`${String(value).slice(0, 10)}T12:00:00`);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
}
